/**
 * Problems modal. The companion to the toolbar's Problems button. Shows
 * every validation issue grouped errors-first, with breadcrumbs and code
 * chips. Click an entry to navigate:
 *   - Node/choice issues → select the affected node, close the modal so the
 *     detail panel reveals the issue in context (the panel's issues banner
 *     handles the in-context display).
 *   - Variable issues → open the Variables modal.
 *   - Objective issues → open the Objectives modal.
 *
 * The validator emits each issue with a `path` describing where it lives,
 * and a `kind` discriminator. The routing here just consumes those.
 */
import { AlertCircle, AlertTriangle, ChevronRight } from 'lucide-react';
import type { Issue, IssuePath } from '@branchworks/shared';
import { useScenarioStore } from '../store/scenarioStore';
import { Modal } from './Modal';

export function ProblemsModal() {
  const scenario = useScenarioStore((s) => s.scenario);
  const getValidation = useScenarioStore((s) => s.getValidation);
  const selectNode = useScenarioStore((s) => s.selectNode);
  const setOpenModal = useScenarioStore((s) => s.setOpenModal);
  const setPanelOpen = useScenarioStore((s) => s.setPanelOpen);

  const result = getValidation();
  const { errors, warnings } = result;
  const total = errors.length + warnings.length;

  const navigate = (issue: Issue) => {
    const { kind, nodeId } = issue.path;
    if (kind === 'variable') {
      setOpenModal('variables');
      return;
    }
    if (kind === 'objective') {
      setOpenModal('objectives');
      return;
    }
    // Node or choice — select the node, ensure the panel is open, close this modal.
    if (nodeId) selectNode(nodeId);
    setPanelOpen(true);
    setOpenModal(null);
  };

  return (
    <Modal
      kicker="Scenario validation"
      title={total === 0 ? 'No problems' : `${total} problem${total === 1 ? '' : 's'}`}
      onClose={() => setOpenModal(null)}
      maxWidth={720}
    >
      {total === 0 ? (
        <div className="text-sm italic text-center py-8 px-4 rounded border border-dashed border-cream-border text-ink-2">
          <div className="font-bold not-italic mb-1 text-ink">Clean scenario.</div>
          The validator found no errors or warnings.
        </div>
      ) : (
        <div className="space-y-4">
          {errors.length > 0 && (
            <IssueGroup
              title="Errors"
              issues={errors}
              icon="error"
              scenario={scenario}
              onNavigate={navigate}
            />
          )}
          {warnings.length > 0 && (
            <IssueGroup
              title="Warnings"
              issues={warnings}
              icon="warning"
              scenario={scenario}
              onNavigate={navigate}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

interface IssueGroupProps {
  title: string;
  issues: Issue[];
  icon: 'error' | 'warning';
  scenario: { nodes: Record<string, { title: string }> };
  onNavigate: (issue: Issue) => void;
}

function IssueGroup({ title, issues, icon, scenario, onNavigate }: IssueGroupProps) {
  const trim = icon === 'error' ? '#A14A38' : '#B8551F';
  const Icon = icon === 'error' ? AlertCircle : AlertTriangle;
  return (
    <div>
      <div
        className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest mb-2"
        style={{ color: trim }}
      >
        <Icon size={13} />
        {title} ({issues.length})
      </div>
      <div className="space-y-1.5">
        {issues.map((issue, i) => (
          <IssueRow
            key={i}
            issue={issue}
            color={trim}
            scenario={scenario}
            onClick={() => onNavigate(issue)}
          />
        ))}
      </div>
    </div>
  );
}

interface IssueRowProps {
  issue: Issue;
  color: string;
  scenario: { nodes: Record<string, { title: string }> };
  onClick: () => void;
}

function IssueRow({ issue, color, scenario, onClick }: IssueRowProps) {
  const breadcrumb = renderBreadcrumb(issue.path, scenario);
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-3 py-2 rounded border border-cream-border bg-white hover:bg-cream flex items-start gap-3"
    >
      <span
        className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full"
        style={{ background: color }}
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-ink">{issue.message}</div>
        {breadcrumb && (
          <div className="text-[10px] uppercase tracking-widest text-ink-2 mt-0.5">
            {breadcrumb}
          </div>
        )}
      </div>
      <span
        className="flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono"
        style={{ background: '#F0EDE5', color: '#6B665D' }}
        title={issue.code}
      >
        {issue.code}
      </span>
      <ChevronRight size={14} className="flex-shrink-0 mt-1 text-ink-2" />
    </button>
  );
}

/**
 * Compose a breadcrumb like
 *   "Patient arrives › Choice 2 · Order an ECG… › visibility"
 * from the issue path.
 */
function renderBreadcrumb(
  path: IssuePath,
  scenario: { nodes: Record<string, { title: string }> },
): string | null {
  const parts: string[] = [];

  if (path.kind === 'variable' && path.variableName) {
    return `Variable: ${path.variableName}`;
  }
  if (path.kind === 'objective' && path.objectiveId) {
    return `Objective: ${path.objectiveId}`;
  }

  if (path.nodeId) {
    const node = scenario.nodes[path.nodeId];
    parts.push(node?.title || path.nodeLabel || path.nodeId);
  }
  if (path.choiceIndex !== undefined) {
    const label = path.choiceLabel ? truncate(path.choiceLabel, 28) : `Choice ${path.choiceIndex + 1}`;
    parts.push(`Choice ${path.choiceIndex + 1} · ${label}`);
  }
  if (path.variantIndex !== undefined) parts.push(`Variant ${path.variantIndex + 1}`);
  if (path.destinationIndex !== undefined) parts.push(`Destination ${path.destinationIndex + 1}`);
  if (path.feedbackIndex !== undefined) parts.push(`Feedback ${path.feedbackIndex + 1}`);
  if (path.sub) parts.push(path.sub);

  return parts.length > 0 ? parts.join(' › ') : null;
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
